import React, { Component } from 'react';

import { displayRawBaselinePercentValue } from 'app/stores/alerting/policyUtils';
import {
  COMPARISON_DIRECTIONS,
  THRESHOLD_CONDITION_TYPES,
  THRESHOLD_CONDITION_TYPE_LABELS,
  OPERATOR_SHORT_LABELS
} from '@kentik/ui-shared/alerting/constants';
import { Box, Callout, Flex, Icon, Heading, Grid, Text } from 'core/components';
import { inject, observer } from 'mobx-react';

const Condition = ({ threshold, alertModel }) => {
  const { direction, operator, formattedMetric, formatted, formattedDiff, rawComparisonValue, type, ratio } = threshold;
  const valueText = direction === COMPARISON_DIRECTIONS.TO_HISTORY ? `${formattedMetric}` : 'Baseline value';
  const otherValueText = direction === COMPARISON_DIRECTIONS.TO_HISTORY ? 'baseline' : 'current value';
  const operatorLabel = OPERATOR_SHORT_LABELS[operator];
  const overText = threshold.over ? 'above' : 'below';
  const { baselineValue } = alertModel;

  let baselinePercent = null;

  if (baselineValue) {
    if (threshold.over) {
      baselinePercent = (threshold.otherValue / baselineValue - 1) * 100;
    } else {
      baselinePercent = (baselineValue - threshold.value / baselineValue) * 100;
    }
  }

  const defaultValueRender = (
    <Box>
      <Text fontWeight="bold">{threshold.formattedOther}</Text>
      {threshold.percentage < 10000 && (
        <Text as="div" muted>
          {formattedDiff} {overText} policy condition
        </Text>
      )}
    </Box>
  );

  const types = {
    [THRESHOLD_CONDITION_TYPES.STATIC]: [
      <Text key={0}>
        <strong>
          {valueText} {operatorLabel} {formatted}
        </strong>
      </Text>,
      defaultValueRender
    ],

    [THRESHOLD_CONDITION_TYPES.CAPACITY]: [
      <Text key={0}>
        <strong>{valueText}</strong> within <strong>{rawComparisonValue} Mbits/s</strong> of interface capacity (
        <strong>
          {operatorLabel} {formatted}
        </strong>
        )
      </Text>,
      defaultValueRender
    ],

    [THRESHOLD_CONDITION_TYPES.CAPACITY_PERCENT]: [
      <Text key={0}>
        <strong>{valueText}</strong> {operatorLabel} <strong>{rawComparisonValue}%</strong> of interface capacity (
        <strong>
          {operatorLabel} {formatted}
        </strong>
        )
      </Text>,
      defaultValueRender
    ],

    [THRESHOLD_CONDITION_TYPES.TOP_KEYS]: [
      <Text key={0}>
        <strong>{valueText}</strong> is not in the top keys but {otherValueText} value is
      </Text>
    ],

    [THRESHOLD_CONDITION_TYPES.BASELINE]: [
      <Text key={0}>
        <strong>{valueText}</strong> {operatorLabel} <strong>{rawComparisonValue} Mbit/sec</strong> above{' '}
        {otherValueText}{' '}
        <Box muted>
          <Text>
            {operatorLabel} {formatted}
          </Text>
        </Box>
      </Text>,
      defaultValueRender
    ],

    [THRESHOLD_CONDITION_TYPES.BASELINE_PERCENT]: [
      <Text key={0}>
        <strong>
          {valueText} {operatorLabel} {displayRawBaselinePercentValue(rawComparisonValue, direction)}
        </strong>{' '}
        <Box>
          <Text muted>{formatted}</Text>
        </Box>
      </Text>,
      <Box key={1}>
        <Text fontWeight="bold">
          {displayRawBaselinePercentValue(Math.abs(baselinePercent || threshold.percentage).toFixed(2), direction)}
        </Text>
        <Text as="div" muted>
          {threshold.formattedOther}
        </Text>
      </Box>
    ],

    [THRESHOLD_CONDITION_TYPES.RATIO]: [
      <Text key={0}>
        Ratio of <strong>{ratio.lhMetric}</strong> to <strong>{ratio.rhMetric}</strong> exceeds{' '}
        <strong>{ratio.alertCondition}</strong>
      </Text>,
      <Text key={1}>
        <strong>{ratio.actualValue}</strong>
      </Text>
    ]
  };

  const content = types[type];

  return (
    <>
      <Box borderBottom="thin" pb="4px">
        <Text small as="div">
          {THRESHOLD_CONDITION_TYPE_LABELS[type]}
        </Text>
      </Box>
      <Box borderBottom="thin" pb="4px">
        <Text small as="div">
          {content[0]}
        </Text>
      </Box>
      <Box borderBottom="thin" pb="4px" pl={1}>
        <Text small as="div">
          {content[1]}
        </Text>
      </Box>
    </>
  );
};

@inject('$alerting')
@observer
class AlertWhy extends Component {
  componentDidMount() {
    const { alertModel } = this.props;
    alertModel.loadHistory();
  }

  render() {
    const { $alerting, alertModel, showHeading = true, className, ...props } = this.props;

    const thresholds = $alerting.getAlertThresholdValues(alertModel);

    return (
      <Box className={className} {...props}>
        {showHeading && (
          <Heading level={5} fontWeight={700}>
            Why Was This Triggered?
          </Heading>
        )}

        {alertModel.policyChanged && (
          <Callout intent="warning" p="12px" my={1}>
            <Flex gap={1}>
              <Icon icon="warning-sign" color="warning" />
              <Text as="div" fontSize={13} lineHeight="auto">
                This policy has been changed. The threshold may have been modified since this alarm started.
              </Text>
            </Flex>
          </Callout>
        )}

        <Text small>The following policy conditions were met:</Text>

        <Grid gridTemplateColumns="1fr 2fr 2fr" gridColumnGap={0} gridRowGap="4px" small mt={1}>
          <Box borderBottom="thin" pb="4px">
            <Text fontWeight="bold" small>
              Type
            </Text>
          </Box>
          <Box borderBottom="thin" pb="4px">
            <Text fontWeight="bold" small>
              Policy Condition{thresholds?.length === 1 ? '' : 's'}
            </Text>
          </Box>
          <Box borderBottom="thin" pb="4px" pl={1}>
            <Text fontWeight="bold" small>
              Triggering Value
            </Text>
          </Box>
          {(thresholds || []).map((threshold, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Condition key={idx} threshold={threshold} alertModel={alertModel} />
          ))}
          {alertModel.alarmTriggerText && (
            <Box gridColumn="1 / span 2">
              <Text small muted>
                {alertModel.alarmTriggerText}
              </Text>
            </Box>
          )}
        </Grid>
      </Box>
    );
  }
}

export default AlertWhy;
